import { useEffect, useMemo, useRef, useState, type FC } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Alert, Button, Card, Spreadsheet, useToast,
  type Matrix, type CellBase, type SearchableSelectOption,
} from '@kss/ui';
import { Plus, Trash2, ArrowLeft, Upload, RefreshCw } from 'lucide-react';
import { useModulePerms } from '../../hooks/useModulePerms';

export type BulkDataType = 'text' | 'integer' | 'number' | 'date' | 'boolean' | 'lookup';

export interface BulkColumn<Row> {
  key: keyof Row & string;
  header: string;
  /** Cell input behaviour in the grid. Default: `text`. */
  dataType?: BulkDataType;
  /** Parse a raw cell string into the typed field value. Default: pass through. */
  parse?: (raw: string) => Row[keyof Row & string];
  /** Required field — row is skipped if blank. */
  required?: boolean;
  /** Sample placeholder used to pre-seed the first row (helps the user). */
  sample?: string;
  /** For `dataType: 'lookup'` — async loader returning options to pick from. */
  loadOptions?: () => Promise<SearchableSelectOption[]>;
}

/** "1", "true", "yes", "active", "" → true · "0", "false", "no", "inactive" → false. */
export const parseBool = (v: string): boolean => {
  const s = v.trim().toLowerCase();
  if (['0', 'false', 'no', 'n', 'inactive'].includes(s)) return false;
  return true;
};

/** Empty string → undefined, else Number(v). */
export const parseNum = (v: string): number | undefined =>
  v.trim() === '' ? undefined : Number(v);

/** Empty string → undefined, else parseInt(v, 10) (rejected if NaN by validate). */
export const parseInt10 = (v: string): number | undefined =>
  v.trim() === '' ? undefined : Number.parseInt(v, 10);

export interface BulkSpreadsheetProps<Row extends Record<string, unknown>> {
  title: string;
  entityLabel: string;
  backPath: string;
  columns: BulkColumn<Row>[];
  /** Called with validated rows. Should POST to the live API. */
  onImport: (rows: Row[]) => Promise<{ created: number } | void>;
  /** Optional initial row count (default 10). */
  initialRows?: number;
  /**
   * Backend module name for permission checks (e.g. "Product",
   * "SalesOrder"). Gates the "Import" toolbar buttons by `canImport`.
   * When omitted, all buttons render unconditionally — pass it whenever
   * the module is known so non-privileged roles can't kick off a bulk
   * insert that the backend will reject anyway.
   */
  module?: string;
}

/* -------------------------------------------------------------------- */
/* Cell editors / viewers — invoked by react-spreadsheet per active cell */
/* -------------------------------------------------------------------- */

interface CellViewProps { cell?: CellBase | undefined; }
interface CellEditProps {
  cell?: CellBase | undefined;
  onChange: (next: CellBase) => void;
  exitEditMode?: () => void;
}

const cellInputBase =
  'w-full h-full px-1 py-0.5 bg-transparent text-sm text-content-primary outline-none border-none';

function makeIntegerEditor(): FC<CellEditProps> {
  return function IntegerEditor({ cell, onChange, exitEditMode }) {
    return (
      <input
        autoFocus
        type="number"
        step={1}
        defaultValue={String(cell?.value ?? '')}
        onChange={(e) => onChange({ ...(cell ?? {}), value: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') exitEditMode?.(); }}
        className={cellInputBase}
      />
    );
  };
}

function makeNumberEditor(): FC<CellEditProps> {
  return function NumberEditor({ cell, onChange, exitEditMode }) {
    return (
      <input
        autoFocus
        type="number"
        step="any"
        defaultValue={String(cell?.value ?? '')}
        onChange={(e) => onChange({ ...(cell ?? {}), value: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') exitEditMode?.(); }}
        className={cellInputBase}
      />
    );
  };
}

function makeDateEditor(): FC<CellEditProps> {
  return function DateEditor({ cell, onChange, exitEditMode }) {
    return (
      <input
        autoFocus
        type="date"
        defaultValue={String(cell?.value ?? '')}
        onChange={(e) => onChange({ ...(cell ?? {}), value: e.target.value })}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') exitEditMode?.(); }}
        className={cellInputBase}
      />
    );
  };
}

function makeBooleanEditor(): FC<CellEditProps> {
  return function BoolEditor({ cell, onChange, exitEditMode }) {
    return (
      <select
        autoFocus
        defaultValue={String(cell?.value ?? 'true')}
        onChange={(e) => onChange({ ...(cell ?? {}), value: e.target.value })}
        onBlur={() => exitEditMode?.()}
        onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') exitEditMode?.(); }}
        className={cellInputBase}
      >
        <option value="true">true</option>
        <option value="false">false</option>
      </select>
    );
  };
}

function makeLookupEditor(
  colKey: string,
  listId: string,
  getOptions: (key: string) => SearchableSelectOption[],
): FC<CellEditProps> {
  return function LookupEditor({ cell, onChange, exitEditMode }) {
    const options = getOptions(colKey);
    return (
      <>
        <input
          autoFocus
          list={listId}
          placeholder="Type to search…"
          defaultValue={String(cell?.value ?? '')}
          onChange={(e) => onChange({ ...(cell ?? {}), value: e.target.value })}
          onKeyDown={(e) => { if (e.key === 'Enter' || e.key === 'Escape') exitEditMode?.(); }}
          className={cellInputBase}
        />
        <datalist id={listId}>
          {options.map((o) => (
            <option key={o.value} value={o.label}>
              {o.description ? `${o.description}` : o.value.slice(0, 8)}
            </option>
          ))}
        </datalist>
      </>
    );
  };
}

function makeLookupViewer(
  colKey: string,
  getOptions: (key: string) => SearchableSelectOption[],
): FC<CellViewProps> {
  return function LookupViewer({ cell }) {
    const raw = String(cell?.value ?? '');
    const options = getOptions(colKey);
    if (!raw) return <span className="text-content-muted">—</span>;
    // Already a label?
    const byLabel = options.find((o) => o.label.toLowerCase() === raw.toLowerCase());
    if (byLabel) return <span>{byLabel.label}</span>;
    // Stored uuid? show label + small id
    const byValue = options.find((o) => o.value === raw);
    if (byValue) return <span>{byValue.label}</span>;
    // Unresolved — show raw text (might be a freshly-typed label that hasn't been refreshed yet)
    return <span>{raw}</span>;
  };
}

/**
 * Resolve a typed-in lookup string back to the option's underlying `value` (uuid).
 * Tries exact value match first, then case-insensitive label match.
 * If no match, returns the raw string so the user can still paste a uuid manually.
 */
function resolveLookupValue(raw: string, options: SearchableSelectOption[]): string {
  const t = raw.trim();
  if (!t) return '';
  const byValue = options.find((o) => o.value === t);
  if (byValue) return byValue.value;
  const byLabel = options.find((o) => o.label.toLowerCase() === t.toLowerCase());
  if (byLabel) return byLabel.value;
  return t;
}

/* -------------------------------------------------------------------- */
/* Main component                                                        */
/* -------------------------------------------------------------------- */

export function BulkSpreadsheet<Row extends Record<string, unknown>>({
  title, entityLabel, backPath, columns, onImport, initialRows = 10, module,
}: BulkSpreadsheetProps<Row>) {
  const navigate = useNavigate();
  const toast = useToast();
  const toastRef = useRef(toast); toastRef.current = toast;
  // Perm gate — only enforced when `module` is passed. Pages that opt
  // out (rare) still render the full toolbar. Admin roles pass through
  // via the useModulePerms allowlist.
  const perms = useModulePerms(module ?? '*');
  const canImport = !module || perms.canImport;

  /* Lookup options cache + loader */
  const [optionsByKey, setOptionsByKey] = useState<Record<string, SearchableSelectOption[]>>({});
  const [loadingLookups, setLoadingLookups] = useState(false);
  const optionsRef = useRef(optionsByKey);
  optionsRef.current = optionsByKey;
  const getOptions = (k: string): SearchableSelectOption[] => optionsRef.current[k] ?? [];

  useEffect(() => {
    const lookups = columns.filter((c) => c.dataType === 'lookup' && c.loadOptions);
    if (lookups.length === 0) return;
    let cancelled = false;
    setLoadingLookups(true);
    Promise.all(
      lookups.map(async (c) => [c.key, await c.loadOptions!().catch(() => [])] as const),
    ).then((entries) => {
      if (cancelled) return;
      const next: Record<string, SearchableSelectOption[]> = {};
      entries.forEach(([k, opts]) => { next[k] = opts; });
      setOptionsByKey(next);
    }).finally(() => { if (!cancelled) setLoadingLookups(false); });
    return () => { cancelled = true; };
  }, [columns]);

  /* Per-column DataEditor / DataViewer (memoized so cells aren't churned) */
  const cellExtras = useMemo(() => {
    return columns.map((col) => {
      const out: Partial<CellBase> = {};
      switch (col.dataType) {
        case 'integer': out.DataEditor = makeIntegerEditor() as CellBase['DataEditor']; break;
        case 'number':  out.DataEditor = makeNumberEditor()  as CellBase['DataEditor']; break;
        case 'date':    out.DataEditor = makeDateEditor()    as CellBase['DataEditor']; break;
        case 'boolean': out.DataEditor = makeBooleanEditor() as CellBase['DataEditor']; break;
        case 'lookup': {
          const listId = `kss-bulk-${col.key}`;
          out.DataEditor = makeLookupEditor(col.key, listId, getOptions) as CellBase['DataEditor'];
          out.DataViewer = makeLookupViewer(col.key, getOptions) as CellBase['DataViewer'];
          break;
        }
        default: /* text */ break;
      }
      return out;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [columns]);

  const buildBlankCell = (ci: number): CellBase => ({ value: '', ...cellExtras[ci] });
  const buildHeaderCell = (ci: number): CellBase => ({
    value: columns[ci].header, readOnly: true, className: 'kss-bulk-header',
  });

  const emptyMatrix = (rowCount: number): Matrix<CellBase> => {
    const rows: CellBase[][] = [columns.map((_, ci) => buildHeaderCell(ci))];
    for (let i = 0; i < rowCount; i++) rows.push(columns.map((_, ci) => buildBlankCell(ci)));
    return rows;
  };

  const [data, setData] = useState<Matrix<CellBase>>(() => {
    const initial = emptyMatrix(initialRows);
    if (initial.length > 1) {
      initial[1] = columns.map((c, ci) => ({ value: c.sample ?? '', ...cellExtras[ci] }));
    }
    return initial;
  });
  const [importing, setImporting] = useState(false);
  const [preview, setPreview] = useState<{ valid: Row[]; invalid: number[]; errors: string[] } | null>(null);

  const addRow = () =>
    setData((d) => [...d, columns.map((_, ci) => buildBlankCell(ci))]);
  const removeLastRow = () => setData((d) => (d.length > 2 ? d.slice(0, -1) : d));
  const clearRows = () => setData(emptyMatrix(initialRows));

  const validate = (): { valid: Row[]; invalid: number[]; errors: string[] } => {
    const valid: Row[] = [];
    const invalid: number[] = [];
    const errors: string[] = [];

    for (let ri = 1; ri < data.length; ri++) {
      const row = data[ri];
      const raws = columns.map((_, ci) => String(row?.[ci]?.value ?? '').trim());
      if (raws.every((v) => !v)) continue;

      // Required-field check
      let rowOk = true;
      for (let ci = 0; ci < columns.length; ci++) {
        if (columns[ci].required && !raws[ci]) { rowOk = false; break; }
      }
      if (!rowOk) { invalid.push(ri); continue; }

      // Per-cell type validation + value resolution
      const obj: Partial<Record<keyof Row & string, unknown>> = {};
      let typeOk = true;
      for (let ci = 0; ci < columns.length; ci++) {
        const col = columns[ci];
        const raw = raws[ci];
        if (!raw) continue;

        let resolved = raw;
        if (col.dataType === 'integer') {
          if (!/^-?\d+$/.test(raw)) {
            errors.push(`Row ${ri + 1}, ${col.header}: must be an integer (got "${raw}")`);
            typeOk = false; break;
          }
        } else if (col.dataType === 'number') {
          if (Number.isNaN(Number(raw))) {
            errors.push(`Row ${ri + 1}, ${col.header}: must be a number (got "${raw}")`);
            typeOk = false; break;
          }
        } else if (col.dataType === 'date') {
          if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
            errors.push(`Row ${ri + 1}, ${col.header}: must be YYYY-MM-DD (got "${raw}")`);
            typeOk = false; break;
          }
        } else if (col.dataType === 'lookup') {
          resolved = resolveLookupValue(raw, getOptions(col.key));
        }

        const parsed = col.parse ? col.parse(resolved) : (resolved as Row[keyof Row & string]);
        obj[col.key] = parsed;
      }
      if (!typeOk) { invalid.push(ri); continue; }
      valid.push(obj as Row);
    }
    return { valid, invalid, errors };
  };

  const runImport = async () => {
    const result = validate();
    setPreview(result);
    if (result.errors.length > 0) {
      toast.error(`${result.errors.length} validation error(s)`, result.errors[0]);
      return;
    }
    if (result.valid.length === 0) {
      toast.error('Nothing to import', 'Fill at least one complete row first.');
      return;
    }
    setImporting(true);
    try {
      const res = await onImport(result.valid);
      const n = res?.created ?? result.valid.length;
      toast.success('Imported', `${n} ${entityLabel}${n === 1 ? '' : 's'} created`);
      navigate(backPath);
    } catch (e) {
      toast.error('Import failed', e instanceof Error ? e.message : 'Failed');
    } finally {
      setImporting(false);
    }
  };

  const validCount = useMemo(() => validate().valid.length, [data, optionsByKey]); // eslint-disable-line react-hooks/exhaustive-deps

  const lookupSummary = columns
    .filter((c) => c.dataType === 'lookup')
    .map((c) => `${c.header} (${getOptions(c.key).length})`)
    .join(' · ');

  if (!canImport) {
    return (
      <div className="-m-6 p-[3px]">
        <Alert variant="warning">
          You don't have permission to bulk-import {entityLabel}s.
        </Alert>
      </div>
    );
  }

  return (
    <div className="-m-6 p-[3px]">
      <Card>
        <div className="mb-3 flex flex-wrap items-center justify-between gap-2">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => navigate(backPath)}>
              <ArrowLeft className="h-4 w-4" />
            </Button>
            <h2 className="text-base font-semibold text-content-primary">{title}</h2>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <Button variant="outline" size="sm" onClick={addRow}>
              <Plus className="h-3.5 w-3.5" /> Add row
            </Button>
            <Button variant="outline" size="sm" onClick={removeLastRow}>
              <Trash2 className="h-3.5 w-3.5" /> Remove row
            </Button>
            <Button variant="outline" size="sm" onClick={clearRows}>
              <RefreshCw className="h-3.5 w-3.5" /> Reset
            </Button>
            <Button size="sm" onClick={runImport} disabled={importing || validCount === 0}>
              <Upload className="h-3.5 w-3.5" /> {importing ? 'Importing…' : `Import (${validCount})`}
            </Button>
          </div>
        </div>

        <Alert variant="info" className="mb-3">
          Paste rows from Excel, or fill cells directly. The first row shows sample values — edit or
          delete it. Required columns are marked with <span className="text-danger">*</span>. Blank rows
          are skipped.{' '}
          {lookupSummary && (
            <>
              <br />
              <span className="text-[11px] text-content-muted">
                Lookup fields {loadingLookups ? '(loading options…)' : ''}: {lookupSummary}
              </span>
            </>
          )}
        </Alert>

        {preview && preview.errors.length > 0 && (
          <Alert variant="danger" className="mb-3">
            <div className="font-medium">Cannot import — fix these first:</div>
            <ul className="mt-1 list-inside list-disc text-xs">
              {preview.errors.slice(0, 10).map((e, i) => <li key={i}>{e}</li>)}
              {preview.errors.length > 10 && <li>…and {preview.errors.length - 10} more</li>}
            </ul>
          </Alert>
        )}

        {preview && preview.invalid.length > 0 && preview.errors.length === 0 && (
          <Alert variant="warning" className="mb-3">
            {preview.invalid.length} row(s) skipped because required fields were blank (rows: {preview.invalid.join(', ')}).
          </Alert>
        )}

        <div className="mb-2 flex flex-wrap items-center gap-3 text-[11px] text-content-muted">
          {columns.map((c) => (
            <span key={c.key} className="inline-flex items-center gap-1">
              <span className="font-medium text-content-secondary">{c.header}</span>
              {c.required && <span className="text-danger">*</span>}
              {c.dataType && c.dataType !== 'text' && (
                <span className="rounded bg-surface-card-hover px-1 py-0.5 text-[9px] uppercase tracking-wider">
                  {c.dataType}
                </span>
              )}
            </span>
          ))}
        </div>

        <div className="overflow-x-auto">
          <Spreadsheet data={data} onChange={setData} />
        </div>

        <div className="mt-4 flex items-center justify-end gap-2 border-t border-border pt-4">
          <Button variant="outline" onClick={() => navigate(backPath)}>Cancel</Button>
          <Button onClick={runImport} disabled={importing || validCount === 0}>
            <Upload className="h-4 w-4" /> {importing ? 'Importing…' : `Import ${validCount} ${entityLabel}${validCount === 1 ? '' : 's'}`}
          </Button>
        </div>
      </Card>
    </div>
  );
}
